import React, { useRef, useEffect } from 'react';
import { usePresentation } from '@context/presentation';
import { useSlideData } from '@context/slide-data';
import { SlideBodyStyle } from '../styles';
import { handleScroll, updateRefScrollPosition, getScrollId } from '@utils/handleScroll';
import { retrieveScrollPosition } from '@utils/browserStorage';
import { SCROLL_IDS, ANALYTICS_EVENTS, Profile } from '@constants';
import { isValidNumber } from '@utils/validateInputs';
import { useMutation } from 'react-query';
import { updateSlideAnalytics } from '@services';

export const SlideBody = ({ children }) => {
    const ref = useRef(null);
    const { clientViewing, presentationId, profile } = usePresentation();
    const { slideState, data } = useSlideData();
    const { id: slideId } = data;
    const { scrollTop, lastSlideId } = slideState;
    const { slideBody } = SCROLL_IDS;
    const scrollId = getScrollId(slideBody, slideId);
    const currentScrollPosition = scrollTop ? scrollTop[scrollId] : null;
    const slideAnalytics = useMutation(data => updateSlideAnalytics(data));
    
    // For clients: update the ref scroll position whenever a new scroll position event is received
    useEffect(() => {
        if (isValidNumber(currentScrollPosition) && clientViewing) {
            updateRefScrollPosition(ref, currentScrollPosition);
        }
    }, [ref, currentScrollPosition]);

    // For both clients and FCs: load saved scroll position for a given slide slide whenever the slideId changes
    useEffect(() => {
        retrieveScrollPosition(scrollId, ref);
    }, [ref, slideId]);

    useEffect(() => {
        // Slide start time analytics should only be recorded if client is connected 
        // (implied by clientViewing) or the profile is CS
        const shouldRecordSlideAnalytics = clientViewing || profile === Profile.CUSTOMER_SERVICE;

        if (shouldRecordSlideAnalytics) {
            const { slideStarted, slideEnded } = ANALYTICS_EVENTS;

            if (lastSlideId) {
                slideAnalytics.mutate({ metricName: slideEnded, slideId: lastSlideId, presentationId });
            }
      
            slideAnalytics.mutate({ metricName: slideStarted, slideId, presentationId });
        }
    }, [ref]);

    return (
        <SlideBodyStyle
            ref={ref}
            id={scrollId}
            onScroll={event => handleScroll(event, scrollId, clientViewing)}   
            clientViewing={clientViewing}>
                {children}
        </SlideBodyStyle>
    );
};

export default SlideBody;
